import * as React from 'react';
import { Avatar, Card, Col, Empty, InputNumber, Row, Select, Spin, Table, Typography, notification } from 'antd';
import { useParams } from 'react-router-dom';
import { useCampaignAvatarMap, useCampaignPayments, useUpdateCreatorMeta } from '../queryHooks';
import { formatEuro } from '../format';

const PaymentsSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const { data, isLoading, refetch } = useCampaignPayments(campaignId);
    const avatars = useCampaignAvatarMap(campaignId);
    const updateMeta = useUpdateCreatorMeta();

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
    if (!data) return <Empty />;

    const save = async (creatorId: number, patch: Record<string, unknown>) => {
        if (!campaignId) return;
        try {
            await updateMeta.mutateAsync({ campaignId, creatorId, ...patch } as any);
            refetch();
        } catch {
            notification.error({ message: 'Failed to save payment' });
        }
    };

    const dash = (n?: number | null) => (n == null ? '--' : formatEuro(n));

    return (
        <div>
            <Row gutter={12} style={{ marginBottom: 16 }}>
                {[
                    { label: 'FEES', value: dash(data.fees) },
                    { label: 'COMMISSIONS (€)', value: dash(data.commissions) },
                    { label: 'BUDGET (€)', value: dash(data.budget) },
                    { label: 'SPEND', value: dash(data.spend) },
                    { label: 'PAID', value: dash(data.paid) },
                    { label: 'BALANCE (€)', value: dash(data.balance) },
                ].map((s) => (
                    <Col span={4} key={s.label}>
                        <Card size="small" bodyStyle={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 20, fontWeight: 600 }}>{s.value}</div>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>{s.label}</Typography.Text>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Table
                rowKey="creator_id"
                dataSource={data.creators}
                pagination={false}
                size="middle"
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        render: (name: string, row) => (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <Avatar src={avatars[row.creator_id] ? `data:image/jpeg;base64,${avatars[row.creator_id]}` : undefined} size={28}>{name?.[0]}</Avatar>
                                {name}
                            </span>
                        ),
                    },
                    {
                        title: 'Fixed fee',
                        dataIndex: 'fixed_fee',
                        render: (v: number | null, row) => (
                            <InputNumber
                                size="small"
                                prefix="€"
                                value={v ?? undefined}
                                onBlur={(e) => {
                                    const n = Number((e.target as HTMLInputElement).value);
                                    save(row.creator_id, { budget: Number.isFinite(n) ? n : null });
                                }}
                            />
                        ),
                    },
                    {
                        title: 'Commission',
                        dataIndex: 'commission',
                        render: (v: number | null, row) => (
                            <InputNumber
                                size="small"
                                prefix="€"
                                value={v ?? undefined}
                                onBlur={(e) => {
                                    const n = Number((e.target as HTMLInputElement).value);
                                    save(row.creator_id, { commission: Number.isFinite(n) ? n : null });
                                }}
                            />
                        ),
                    },
                    {
                        title: 'Paid',
                        dataIndex: 'paid',
                        render: (v: number | null, row) => (
                            <InputNumber
                                size="small"
                                prefix="€"
                                value={v ?? undefined}
                                onBlur={(e) => {
                                    const n = Number((e.target as HTMLInputElement).value);
                                    save(row.creator_id, { paid_amount: Number.isFinite(n) ? n : null });
                                }}
                            />
                        ),
                    },
                    {
                        title: 'Date',
                        dataIndex: 'paid_at',
                        render: (v: string | null) => v ? new Date(v).toLocaleDateString() : '--',
                    },
                    {
                        title: 'Balance',
                        dataIndex: 'balance',
                        render: (v: number | null) => dash(v),
                    },
                    {
                        title: 'Payment status',
                        dataIndex: 'payment_status',
                        render: (v: string, row) => (
                            <Select
                                size="small"
                                style={{ width: 110 }}
                                value={v || 'unpaid'}
                                options={[
                                    { label: 'Unpaid', value: 'unpaid' },
                                    { label: 'Partial', value: 'partial' },
                                    { label: 'Paid', value: 'paid' },
                                ]}
                                onChange={(status) => save(row.creator_id, { payment_status: status })}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default PaymentsSection;
